import React from 'react';
import { Button, Card, Space, Typography, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CheckOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const ModificationPromptContainer = styled.div`
  margin: 16px 0;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const CurrentStrategyCard = styled(Card)`
  margin-bottom: 16px;
  background-color: #f6ffed;
  border-color: #b7eb8f;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const StrategyInfo = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border-left: 4px solid #1890ff;
`;

interface StrategyModificationPromptProps {
  message: string;
  currentStrategy: string;
  onComplete: () => void;
  onModify: () => void;
  onReselect?: () => void; // 新增：重新选择策略
  availableStrategies?: Array<{id: string, name: string}>; // 新增：可用策略列表
}

export const StrategyModificationPrompt: React.FC<StrategyModificationPromptProps> = ({
  message,
  currentStrategy,
  onComplete,
  onModify,
  onReselect,
  availableStrategies
}) => {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.language === 'ja';

  // 根据语言返回不同的标题和描述
  const getContent = () => {
    if (isJapanese) {
      return {
        title: "分析が完了しました！",
        subtitle: "現在の選択された戦略で詳細な分析が完了しました。",
        currentLabel: "現在選択されている戦略：",
        completeText: "この分析で完了",
        modifyText: "分析内容を調整",
        reselectText: "他の戦略を試す",
        reselectDesc: "別の戦略方法で分析を再実行します",
        availableText: "利用可能な戦略オプション："
      };
    } else {
      return {
        title: "分析完成！",
        subtitle: "基于当前选择的策略，详细分析已完成。",
        currentLabel: "当前选择的策略：",
        completeText: "完成分析",
        modifyText: "调整分析内容",
        reselectText: "尝试其他策略",
        reselectDesc: "使用不同的策略方法重新进行分析",
        availableText: "可用的策略选项："
      };
    }
  };

  const content = getContent();

  return (
    <ModificationPromptContainer>
      <Title level={4} style={{ marginBottom: 8, color: '#1890ff' }}>
        {content.title}
      </Title>
      
      <Text style={{ color: '#666', fontSize: '14px', display: 'block', marginBottom: 16 }}>
        {content.subtitle}
      </Text>
      
      <StrategyInfo>
        <Text strong style={{ color: '#1890ff' }}>
          {content.currentLabel}
        </Text>
        <br />
        <Text style={{ fontSize: '15px', fontWeight: 500 }}>
          {currentStrategy}
        </Text>
      </StrategyInfo>
      
      {availableStrategies && availableStrategies.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            {content.availableText}
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {availableStrategies.map((strategy, index) => (
              <span
                key={strategy.id}
                style={{
                  padding: '4px 8px',
                  background: strategy.name === currentStrategy ? '#e6f7ff' : '#f5f5f5',
                  border: strategy.name === currentStrategy ? '1px solid #91d5ff' : '1px solid #d9d9d9',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: strategy.name === currentStrategy ? '#1890ff' : '#666'
                }}
              >
                {strategy.name}
              </span>
            ))}
          </div>
        </div>
      )}
      
      <Divider style={{ margin: '16px 0' }} />
      
      <ActionButtons>
        <Button 
          type="primary" 
          size="large"
          icon={<CheckOutlined />}
          onClick={onComplete}
          style={{ minWidth: '120px' }}
        >
          {content.completeText}
        </Button>
        
        {onReselect && (
          <Button 
            size="large"
            icon={<ReloadOutlined />}
            onClick={onReselect}
            style={{ minWidth: '120px' }}
          >
            {content.reselectText}
          </Button>
        )}
        
        <Button 
          size="large"
          icon={<EditOutlined />}
          onClick={onModify}
          style={{ minWidth: '120px' }}
        >
          {content.modifyText}
        </Button>
      </ActionButtons>
      
      {onReselect && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {content.reselectDesc}
          </Text>
        </div>
      )}
    </ModificationPromptContainer>
  );
}; 